import React, { useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import {
  ModalInjectedProps,
  Modal,
  Button,
  LoadingPlaceholder,
  Flex,
  useModalContext,
  Text,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import {
  ModalStyled,
  ErrorIcon,
  TabContentContainer,
} from './PartialShareModal.styles'
import { SettingsTab } from './SettingsTab'
import { ContentsTab } from './ContentsTab'
import {
  PreviewFormValues,
  PreviewFormProvider,
  usePreviewForm,
} from './usePreviewForm'
import {
  GetSharePreviewQuery,
  useDeletePreviewMutation,
  useUpdatePreviewMutation,
  useGetSharePreviewQuery,
  GetSharePreviewsQuery,
} from '@sketch/gql-types'
import { ModalConfirmation } from './ModalConfirmation'

const validationSchema = yup.object({
  previewName: yup
    .string()
    .required('Preview name is required')
    .test(
      'not-only-whitespace',
      'Your preview name cannot contain only spaces',
      value => (value ? value.trim().length > 0 : true)
    ),
  description: yup.string(),
  password: yup.string().when(['passwordProtected', 'hasExistingPassword'], {
    is: (passwordProtected: boolean, hasExistingPassword: boolean) =>
      passwordProtected && !hasExistingPassword,
    then: schema =>
      schema
        .required('Password is required when password protection is enabled')
        .min(6, 'Password must be at least 6 characters')
        .test(
          'not-only-whitespace',
          'Password cannot contain only spaces',
          value => (value ? value.trim().length > 0 : true)
        ),
    otherwise: schema =>
      schema
        .test(
          'password-change',
          'Password must be at least 6 characters',
          value => !value || value.length >= 6
        )
        .test(
          'not-only-whitespace',
          'Password cannot be only whitespace',
          value => !value || value.trim().length > 0
        ),
  }),
  expiryDate: yup.string().when('expiryDateEnabled', {
    is: true,
    then: schema =>
      schema.required('Date is required when preview expiration is enabled'),
  }),
  selectedUUIDs: yup
    .array(
      yup.object({
        uuid: yup.string().required(),
        type: yup.string().oneOf(['PAGE', 'PROTOTYPE']).required(),
      })
    )
    .min(1, 'At least one item must be selected'),
})

// Use the preview type from the GraphQL query
type PreviewType = NonNullable<GetSharePreviewQuery['share']>['preview']
type CachedShare = NonNullable<GetSharePreviewsQuery['share']>

const formatSelectedUUIDs = (existingPreview?: PreviewType) => {
  const pages =
    existingPreview?.pages.entries
      .filter(item => item.uuid !== null)
      .map(item => ({
        type: 'PAGE' as const,
        uuid: item.uuid!,
      })) || []
  const prototypes =
    existingPreview?.prototypes.entries.map(item => ({
      type: 'PROTOTYPE' as const,
      uuid: item.uuid,
    })) || []

  return [...pages, ...prototypes]
}

const getInitialValues = (
  existingPreview?: PreviewType
): PreviewFormValues => ({
  previewName: existingPreview?.name || '',
  description: existingPreview?.description || '',
  inspectEnabled: existingPreview?.inspectEnabled || false,
  commentsEnabled: existingPreview?.commentsEnabled || false,
  passwordProtected: !!existingPreview?.passwordProtected,
  password: '',
  hasExistingPassword: !!existingPreview?.passwordProtected,
  expiryDate: existingPreview?.expiryDate
    ? new Date(existingPreview.expiryDate).toISOString().split('T')[0]
    : '',
  expiryDateEnabled: !!existingPreview?.expiryDate,
  selectedUUIDs: formatSelectedUUIDs(existingPreview) || [],
})

interface PartialShareModalEditProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  shareThumbnail?: string
  versionShortId: string
  existingPreviewId: string
}

export const PartialShareModalEdit = ({
  shareName,
  shareIdentifier,
  shareThumbnail,
  versionShortId,
  existingPreviewId,
  hideModal,
}: PartialShareModalEditProps) => {
  const { showToast } = useToast()
  const { showModal } = useModalContext()
  const [previewIdentifier, setPreviewIdentifier] =
    useState<string>(existingPreviewId)

  const { data, loading, error, refetch } = useGetSharePreviewQuery({
    variables: { previewIdentifier: previewIdentifier || '', shareIdentifier },
    skip: !previewIdentifier,
  })

  const existingPreview = data?.share?.preview

  const [updatePreview] = useUpdatePreviewMutation({
    onCompleted: ({ updatePreview }) => {
      showToast(
        <>
          The Preview &quot;{updatePreview.preview.name}&quot; has been
          successfully updated.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const [deleteMutation] = useDeletePreviewMutation({
    onCompleted: ({ deletePreview }) => {
      showToast(
        <>
          The Preview &quot;{deletePreview.preview.name}&quot; has been
          successfully deleted.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
    update: (cache, { data }) => {
      if (!data) {
        return
      }

      const deletedPreviewIdentifier = data.deletePreview.preview.identifier

      const previewCacheId = cache.identify({
        __typename: 'Preview',
        identifier: deletedPreviewIdentifier,
      })

      if (previewCacheId) {
        cache.evict({ id: previewCacheId })
      }

      const shareCacheId = cache.identify({
        __typename: 'Share',
        identifier: shareIdentifier,
      })

      if (shareCacheId) {
        cache.modify<CachedShare>({
          id: shareCacheId,
          fields: {
            previews(existingPreviews = [], { readField }) {
              if (!Array.isArray(existingPreviews)) return existingPreviews

              return existingPreviews.filter(
                previewRef =>
                  previewRef &&
                  readField('identifier', previewRef) !==
                    deletedPreviewIdentifier
              )
            },
          },
        })
      }

      cache.gc()
    },
  })

  const handleRemovePreview = () => {
    if (!previewIdentifier) {
      return
    }

    deleteMutation({
      variables: {
        identifier: previewIdentifier,
      },
    })
  }

  const handleReopenPartialSharingModal = (identifier?: string) => {
    showModal(PartialShareModalEdit, {
      shareName,
      shareIdentifier,
      shareThumbnail,
      versionShortId,
      existingPreviewId: identifier ?? previewIdentifier,
    })
  }

  const handleSubmit = (values: PreviewFormValues) => {
    const expiryDate =
      values.expiryDateEnabled && !!values.expiryDate
        ? new Date(values.expiryDate).toISOString()
        : undefined

    const trimmedPassword = values.password.trim()

    // Only include password in the mutation if the user has entered a new one
    const hasNewPassword = trimmedPassword !== ''

    // Build base variables object
    const variables: {
      identifier: string
      name: string
      description: string
      inspectEnabled: boolean
      commentsEnabled: boolean
      expiryDate?: string
      content: Array<{ identifier: string; type: 'PAGE' | 'PROTOTYPE' }>
      password?: string
    } = {
      identifier: previewIdentifier,
      name: values.previewName,
      description: values.description,
      inspectEnabled: values.inspectEnabled,
      commentsEnabled: values.commentsEnabled,
      expiryDate,
      // although we're dealing with UUIDs, the mutation expects them to be under identifier
      content: values.selectedUUIDs.map(selected => ({
        identifier: selected.uuid,
        type: selected.type,
      })),
    }

    // Conditionally add password field
    if (hasNewPassword) {
      // User entered a new password - include it
      variables.password = trimmedPassword
    } else if (values.passwordProtected === false) {
      // Password protection is being disabled - clear password
      variables.password = ''
    }
    // If values.passwordProtected is true and hasNewPassword is false,
    // don't include password field at all (preserves existing password)

    return updatePreview({ variables })
  }

  if (loading) {
    return (
      <ModalStyled onCancel={hideModal}>
        <Flex justifyContent="center" alignItems="center" height={635}>
          <LoadingPlaceholder size="64px" />
        </Flex>
      </ModalStyled>
    )
  }

  if (error) {
    return (
      <ModalStyled onCancel={hideModal}>
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          height={635}
          p={5}
        >
          <ErrorIcon />
          <Text pt={4} pb={4} textAlign="center" fontSize="F">
            Failed to load preview settings
          </Text>
          <Flex>
            <Button onClick={hideModal}>Cancel</Button>
            <Button variant="primary" onClick={() => refetch()}>
              Try Again
            </Button>
          </Flex>
        </Flex>
      </ModalStyled>
    )
  }

  return (
    <Formik
      initialValues={getInitialValues(existingPreview)}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      <ModalStyled
        onCancel={hideModal}
        autoFocusOnOpen={false}
        cancelOnClickOutside={false}
      >
        <PreviewFormProvider
          previewIdentifier={previewIdentifier}
          setPreviewIdentifier={setPreviewIdentifier}
        >
          <Modal.Tabs
            steps={[
              {
                title: 'Settings',
                content: (
                  <TabContentContainer>
                    <SettingsTab
                      shareName={shareName}
                      shareThumbnail={shareThumbnail}
                      shareIdentifier={shareIdentifier}
                      reopenPartialSharingModal={
                        handleReopenPartialSharingModal
                      }
                      isEditMode
                    />
                  </TabContentContainer>
                ),
              },
              {
                title: 'Preview Contents',
                content: (
                  <TabContentContainer>
                    <ContentsTab
                      shareIdentifier={shareIdentifier}
                      versionShortId={versionShortId}
                    />
                  </TabContentContainer>
                ),
              },
            ]}
          />
          <PreviewFormFooter
            onRemovePreview={handleRemovePreview}
            hideModal={hideModal}
            onCancel={() => {
              handleReopenPartialSharingModal()
            }}
          />
        </PreviewFormProvider>
      </ModalStyled>
    </Formik>
  )
}

function PreviewFormFooter({
  hideModal,
  onRemovePreview,
  onCancel,
}: {
  hideModal: () => void
  onRemovePreview: () => void
  onCancel: () => void
}) {
  const { submitForm, isSubmitting, isValid, dirty } = usePreviewForm()
  const { showModal } = useModalContext()

  const handleRemove = () => {
    showModal(ModalConfirmation, {
      title: 'Remove Preview?',
      description:
        'The link to this preview will no longer work and no one will be able to access it.',
      confirmText: 'Remove Preview',
      onConfirm: onRemovePreview,
      didCancel: () => {
        onCancel()
      },
    })
  }

  return (
    <Modal.Footer
      leftContent={
        <Button
          variant="negative-secondary"
          disabled={isSubmitting}
          onClick={handleRemove}
        >
          Remove Preview
        </Button>
      }
    >
      <Button disabled={isSubmitting} onClick={hideModal}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={submitForm}
        // submit is disabled until there are changes, when in edit mode
        disabled={isSubmitting || !isValid || !dirty}
      >
        {isSubmitting ? 'Updating...' : 'Update Preview'}
      </Button>
    </Modal.Footer>
  )
}
